// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/Garantias/Garantias.tsx
//  Listagem paginada de garantias. Busca (descrição, nº de série, cliente),
//  status, cliente, período e ordenação rodam NO SERVIDOR.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ShieldCheck, Trash2, Eye, ChevronLeft, ChevronRight, AlertTriangle, Search } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import { PeriodoFiltro, usePeriodo } from '@/components/listas/PeriodoFiltro';
import { CabecalhoOrdenavel, useOrdenacao } from '@/components/listas/CabecalhoOrdenavel';
import { PessoaLookupCombobox } from '@/components/PessoaLookupCombobox';
import { useGarantia } from '@/features/garantias/hooks/useGarantia';
import type { StatusGarantia } from '@/features/garantias/types';
import { DeleteConfirmDialog } from '@/features/financeiro/components/DeleteConfirmDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const PAGE_SIZES = [10, 20, 50] as const;

const statusConfig: Record<StatusGarantia, { label: string; className: string }> = {
  vigente:  { label: 'Vigente',  className: 'bg-green-500/10 text-green-600'  },
  acionada: { label: 'Acionada', className: 'bg-orange-500/10 text-orange-600' },
  expirada: { label: 'Expirada', className: 'bg-red-500/10 text-red-600'      },
};

const diasRestantes = (dtVencimento: string) => {
  const diff = Math.ceil((new Date(dtVencimento).getTime() - Date.now()) / 86_400_000);
  return diff;
};

const Garantias = () => {
  const navigate = useNavigate();
  const [statusFiltro,   setStatusFiltro]   = useState('todos');
  const [clienteId,      setClienteId]      = useState(0);
  const [pagina,         setPagina]         = useState(1);
  const [tamanhoPagina,  setTamanhoPagina]  = useState(20);
  const [deletingId,     setDeletingId]     = useState<number | null>(null);
  const [busca,          setBusca]          = useState('');

  // Abre nas garantias iniciadas nos últimos 30 dias, as mais recentes primeiro.
  // Para ver o que vence logo: período "Todo o período" + ordenar por Vencimento.
  const periodo   = usePeriodo('30dias', () => setPagina(1));
  const ordenacao = useOrdenacao<'inicio' | 'vencimento' | 'descricao'>('inicio', true, () => setPagina(1));
  const buscaEnviada = useDebounce(busca.trim(), 400);

  const { garantias, total, totalPaginas, resumo, isLoading, isFetching, deleteGarantia } =
    useGarantia({
      pagina, tamanhoPagina, status: statusFiltro, clienteId,
      filtros: {
        busca: buscaEnviada, ...periodo.intervalo,
        ordenarPor: ordenacao.ordenarPor, decrescente: ordenacao.decrescente,
      },
    });

  // KPIs do servidor: cliente + busca + período inteiros, não só a página.
  const totalVigentes  = resumo?.vigentes  ?? 0;
  const totalAcionadas = resumo?.acionadas ?? 0;
  const totalExpiradas = resumo?.expiradas ?? 0;
  const firstItem = (pagina - 1) * tamanhoPagina + 1;
  const lastItem  = Math.min(pagina * tamanhoPagina, total);

  const handlePageSizeChange = (v: string) => { setTamanhoPagina(Number(v)); setPagina(1); };
  const handleStatusChange   = (v: string) => { setStatusFiltro(v); setPagina(1); };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Controle de Garantias</h1>
          <p className="text-muted-foreground mt-1">Acompanhe garantias de produtos e serviços</p>
        </div>
        <Button className="gap-2" onClick={() => navigate('/operacional/garantias/nova')}>
          <Plus className="h-4 w-4" />Nova Garantia
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-green-500" />Vigentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">{totalVigentes}</div>
            <p className="text-xs text-muted-foreground mt-1">Garantias ativas</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-orange-500" />Acionadas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-500">{totalAcionadas}</div>
            <p className="text-xs text-muted-foreground mt-1">Com ocorrências em aberto</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-red-500" />Expiradas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">{totalExpiradas}</div>
            <p className="text-xs text-muted-foreground mt-1">Garantias vencidas</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-3 items-start md:items-center justify-between">
            <CardTitle>Garantias</CardTitle>
            <div className="flex flex-col sm:flex-row sm:flex-wrap md:justify-end gap-2 w-full md:w-auto">
              <div className="relative flex-1 md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Descrição, nº de série ou cliente..."
                  value={busca}
                  onChange={(e) => { setBusca(e.target.value); setPagina(1); }}
                  className="pl-9"
                />
              </div>
              <div className="w-full sm:w-64">
                <PessoaLookupCombobox
                  papel="cliente"
                  value={clienteId || null}
                  onChange={(id) => { setClienteId(id ?? 0); setPagina(1); }}
                  placeholder="Todos os clientes"
                />
              </div>
              <PeriodoFiltro estado={periodo} rotuloData="Início" />
              <Select value={statusFiltro} onValueChange={handleStatusChange}>
                <SelectTrigger className="w-full sm:w-44">
                  <SelectValue placeholder="Todos os status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os status</SelectItem>
                  <SelectItem value="vigente">Vigente</SelectItem>
                  <SelectItem value="acionada">Acionada</SelectItem>
                  <SelectItem value="expirada">Expirada</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente</TableHead>
                  <CabecalhoOrdenavel coluna="descricao" ordenacao={ordenacao} decrescentePadrao={false}>Descrição</CabecalhoOrdenavel>
                  <TableHead>Nº Série</TableHead>
                  <CabecalhoOrdenavel coluna="inicio" ordenacao={ordenacao}>Início</CabecalhoOrdenavel>
                  <CabecalhoOrdenavel coluna="vencimento" ordenacao={ordenacao} decrescentePadrao={false}>Vencimento</CabecalhoOrdenavel>
                  <TableHead className="text-right">Dias Restantes</TableHead>
                  <TableHead className="text-right">Ocorrências</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
                ) : garantias.length === 0 ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    {periodo.periodo !== 'todos'
                      ? 'Nenhuma garantia iniciada neste período — amplie o período para ver as anteriores'
                      : 'Nenhuma garantia encontrada'}
                  </TableCell></TableRow>
                ) : (
                  garantias.map((g) => {
                    const cfg  = statusConfig[g.status];
                    const dias = diasRestantes(g.dtVencimento);
                    return (
                      <TableRow key={g.id}>
                        <TableCell>{g.clienteNome || <span className="text-muted-foreground">—</span>}</TableCell>
                        <TableCell className="font-medium">{g.descricao}</TableCell>
                        <TableCell>{g.numSerie || <span className="text-muted-foreground">—</span>}</TableCell>
                        <TableCell>{new Date(g.dtInicio).toLocaleDateString('pt-BR')}</TableCell>
                        <TableCell>{new Date(g.dtVencimento).toLocaleDateString('pt-BR')}</TableCell>
                        <TableCell className="text-right">
                          <span className={dias <= 30 && dias > 0 ? 'text-orange-500 font-medium' : dias <= 0 ? 'text-red-500' : ''}>
                            {dias > 0 ? `${dias}d` : 'Vencida'}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          {g.ocorrenciasAbertas > 0 && (
                            <span className="text-orange-600 font-medium">{g.ocorrenciasAbertas} abertas / </span>
                          )}
                          {g.totalOcorrencias} total
                        </TableCell>
                        <TableCell>
                          <Badge className={cfg.className}>{cfg.label}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" variant="ghost"
                              onClick={() => navigate(`/operacional/garantias/${g.id}`)} title="Ver detalhe">
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setDeletingId(g.id)} title="Excluir">
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Paginação */}
          <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t">
            <p className="text-sm text-muted-foreground">
              {total === 0 ? 'Nenhum registro'
                : `Mostrando ${firstItem}–${lastItem} de ${total} registro${total !== 1 ? 's' : ''}`}
              {isFetching && !isLoading && <span className="ml-2 text-xs">(atualizando...)</span>}
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground whitespace-nowrap">Itens por página:</span>
                <Select value={String(tamanhoPagina)} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="w-[80px] h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(1)} disabled={pagina === 1}>«</Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina((p) => p - 1)} disabled={pagina === 1}><ChevronLeft className="h-4 w-4" /></Button>
                {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
                  let page: number;
                  if      (totalPaginas <= 5)         page = i + 1;
                  else if (pagina <= 3)                page = i + 1;
                  else if (pagina >= totalPaginas - 2) page = totalPaginas - 4 + i;
                  else                                 page = pagina - 2 + i;
                  return (
                    <Button key={page} variant={pagina === page ? 'default' : 'outline'}
                      size="icon" className="h-8 w-8 text-xs" onClick={() => setPagina(page)}>{page}</Button>
                  );
                })}
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina((p) => p + 1)} disabled={pagina >= totalPaginas}><ChevronRight className="h-4 w-4" /></Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(totalPaginas)} disabled={pagina >= totalPaginas}>»</Button>
              </div>
              <span className="text-sm text-muted-foreground whitespace-nowrap">Página {pagina} de {totalPaginas}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <DeleteConfirmDialog
        open={!!deletingId}
        onOpenChange={(open) => !open && setDeletingId(null)}
        onConfirm={() => { if (deletingId) { deleteGarantia(deletingId); setDeletingId(null); } }}
        title="Excluir Garantia"
        description="Todas as ocorrências desta garantia serão excluídas. Esta ação não pode ser desfeita."
      />
    </div>
  );
};

export default Garantias;
